import moment from 'moment';
import React, { useEffect, useRef, useState } from 'react';
import CommonDialog from '@src/new-components/Dialog/CommonDialog';
import { useTranslation } from 'react-i18next';
import CommonButton from '@src/new-components/Button';
import { COLOR } from '@src/styles/color';
import apis from '@src/apis';
import {
  MINIMUM_VOICE_REVENUE_WITHDRAWAL_AMOUNT,
  STEP_WITHDRAWAL_STATUS,
  WITHDRAWAL_SOURCE,
} from '@src/constants/withdrawal';
import { COMMISSION_PAYMENT_LIMIT } from '@src/constants/affiliate';
import { useDispatch, useSelector } from 'react-redux';
import actions from '@src/redux/actions';
import { uploadFileToS3 } from '@src/services/upload';
import { v4 as uuid } from 'uuid';
import { Box, CircularProgress } from '@mui/material';
import { VOICE_REVENUE_TRANSACTION_STATUS } from '@src/constants/voiceCloning';
import useForm from '@src/hooks/useForm';
import { createValidationSchema } from '@src/utils/form';
import { EMAIL_REGEX } from '@src/constants';
import { convertPhoneCountryNumber } from '@src/services/phoneNumber';
import Typography from '@src/new-components/Typography';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import { convertFormatDate } from '@src/utils/date';
import { StyledWithdrawal, StyledActionButton } from './index.style';
import AmountInputForm from './AmountWithdrawal';
import PersonalInfo from './PersonalInfo';
import BankInfo from './BankInfo';
import WithDrawalFooter from './WithDrawalFooter';
import StatusStepper from './StatusStepper';

const Withdrawal = ({
  isOpenPopupWithdrawal,
  closePopupWithdrawal,
  balance,
  onSuccess,
  processingWithdrawal,
  isShowDetail = false,
  onCancelWithdrawal,
  paidTransaction,
  source = WITHDRAWAL_SOURCE.VOICE_REVENUE,
}) => {
  const isAffiliate = source === WITHDRAWAL_SOURCE.AFFILIATE;
  const minimumAmount = isAffiliate
    ? COMMISSION_PAYMENT_LIMIT
    : MINIMUM_VOICE_REVENUE_WITHDRAWAL_AMOUNT;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [isAgreeTerms, setIsAgreeTerms] = useState(false);
  const [isAutoFillPaidTxn, setIsAutoFillPaidTxn] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const isProcessing =
    processingWithdrawal?.status ===
    VOICE_REVENUE_TRANSACTION_STATUS.PROCESSING;
  const isPending =
    processingWithdrawal?.status === VOICE_REVENUE_TRANSACTION_STATUS.PENDING;
  const isInit =
    processingWithdrawal?.status === VOICE_REVENUE_TRANSACTION_STATUS.INIT;
  const isPaid =
    processingWithdrawal?.status === VOICE_REVENUE_TRANSACTION_STATUS.PAID;
  const isReject =
    processingWithdrawal?.status === VOICE_REVENUE_TRANSACTION_STATUS.REJECTED;
  const isCancel =
    processingWithdrawal?.status === VOICE_REVENUE_TRANSACTION_STATUS.CANCELLED;

  const isShowValue = isPending || isInit || isProcessing || isShowDetail;
  const isShowStatusStepper =
    isShowDetail && (isProcessing || isPending || isInit || isPaid);

  const contentRef = useRef(null);

  const handleConvertBase64ToFile = async (base64) =>
    fetch(base64)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], `${uuid()}.png`, { type: 'image/png' });
        return file;
      });

  const handleUploadImageToS3 = async (file) => {
    const { name: fileName } = file;
    const extension = fileName?.split('.')[1];
    const imgName = fileName?.slice(0, fileName?.lastIndexOf('.'));
    const name = `${imgName}_${uuid()}.${extension}`;
    const directory = 'images/users';
    const s3Url = await uploadFileToS3(name, directory, extension, file);

    if (!s3Url) {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'uploadError',
        }),
      );
      setIsLoading(false);
      return null;
    }

    return s3Url;
  };

  // Affiliate uses `money`, voice-revenue uses `amount`; payload is otherwise identical.
  const buildWithdrawalPayload = async (values) => {
    const amountKey = isAffiliate ? 'money' : 'amount';
    if (isAutoFillPaidTxn) {
      return { [amountKey]: values.amount, isPaidTxnExist: true };
    }

    const digitalSignatureFile = await handleConvertBase64ToFile(
      values.digitalSignature,
    );
    const [identityFrontImageUrl, identityBackImageUrl, digitalSignature] =
      await Promise.all(
        [
          values.identityFrontImageUrl,
          values.identityBackImageUrl,
          digitalSignatureFile,
        ].map(handleUploadImageToS3),
      );

    return {
      [amountKey]: values.amount,
      user: {
        fullName: values.fullName.trim(),
        phoneNumber: values.phoneNumber,
        email: values.email.trim(),
        address: values.address.trim(),
        identityNumber: values.identityNumber.trim(),
        taxCode: values.taxCode.trim(),
        identityFrontImageUrl,
        identityBackImageUrl,
        digitalSignature,
        dateOfIssue: moment(values?.dateOfIssue, 'DD/MM/YYYY').toISOString(),
        placeOfIssue: values.placeOfIssue.trim(),
      },
      bank: {
        name: values.bankName,
        accountNumber: values.accountNumber.trim(),
        accountOwner: values.accountOwner,
      },
    };
  };

  const handleSendWithdrawalRequest = async (values, { resetForm }) => {
    setIsLoading(true);
    const payload = await buildWithdrawalPayload(values);
    const res = isAffiliate
      ? await apis.affiliateTransaction.createAffiliateTransactionRequest(
          payload,
        )
      : await apis.voiceCloning.sendWithdrawalRequest(payload);
    setIsLoading(false);
    if (res.status) {
      if (onSuccess) onSuccess();
      closePopupWithdrawal();
      resetForm();
    } else {
      dispatch(
        actions.noti.push({
          severity: 'error',
          message: 'errorOccurred',
        }),
      );
    }
  };

  const validationSchema = isAutoFillPaidTxn
    ? createValidationSchema()
        .min('amount', minimumAmount, 'amountMin')
        .max('amount', balance, 'amountWithdrawalMax')
        .toObject()
    : createValidationSchema()
        .min('amount', minimumAmount, 'amountMin')
        .max('amount', balance, 'amountWithdrawalMax')
        .required('amount', 'amountMin')
        .required('fullName', t('fullNameRequired'))
        .maxLength(
          'fullName',
          100,
          t('maxLengthCharacters', { maxLength: 100 }),
        )
        .required('phoneNumber', t('phoneNumberIsRequired'))
        .maxLength(
          'phoneNumber',
          50,
          t('maxLengthCharacters', { maxLength: 50 }),
        )
        .validate('phoneNumber', (phoneNumber, values) => {
          if (!convertPhoneCountryNumber(phoneNumber, values.countryCode)) {
            return t('phoneNumberIsInvalid');
          }
          return null;
        })
        .required('email', t('emailRequired'))
        .pattern('email', EMAIL_REGEX, t('emailInvalid'))
        .maxLength('email', 50, t('maxLengthCharacters', { maxLength: 50 }))
        .required('address', t('addressRequired'))
        .maxLength('address', 100, t('maxLengthCharacters', { maxLength: 100 }))
        .required('identityNumber', t('identityNumberRequired'))
        .maxLength(
          'identityNumber',
          12,
          t('maxLengthCharacters', { maxLength: 12 }),
        )
        .required('taxCode', t('taxCodeRequired'))
        .maxLength('taxCode', 50, t('maxLengthCharacters', { maxLength: 50 }))
        .required('identityFrontImageUrl', t('identityFrontImageUrlRequired'))
        .required('identityBackImageUrl', t('identityBackImageUrlRequired'))
        .required('digitalSignature', t('digitalSignatureRequired'))
        .required('bankName', t('bankNameRequired'))
        .required('accountOwner', t('bankAccountOwnerIsRequired'))
        .required('accountNumber', t('bankAccountNumberIsRequired'))
        .maxLength(
          'accountOwner',
          100,
          t('maxLengthCharacters', { maxLength: 100 }),
        )
        .maxLength(
          'accountNumber',
          50,
          t('maxLengthCharacters', { maxLength: 50 }),
        )
        .required('dateOfIssue', t('dateOfIssueRequired'))
        .required('placeOfIssue', t('placeOfIssueRequired'))
        .maxLength(
          'placeOfIssue',
          320,
          t('maxLengthCharacters', { maxLength: 320 }),
        )
        .toObject();

  const {
    values,
    errors,
    touched,
    handleSubmit,
    handleChange,
    setValues,
    setFieldError,
    isValidating,
    isSubmitting,
    resetForm,
  } = useForm({
    initialValues: {
      amount: '',
      fullName: '',
      phoneNumber: '',
      email: user?.email,
      address: '',
      identityNumber: '',
      taxCode: '',
      identityFrontImageUrl: '',
      identityBackImageUrl: '',
      digitalSignature: '',
      bankName: '',
      accountNumber: '',
      accountOwner: '',
      dateOfIssue: '',
      placeOfIssue: '',
    },
    validationSchema,
    onSubmit: handleSendWithdrawalRequest,
    options: {
      resetOnSubmit: false,
    },
  });

  const handleClosePopup = (e, reason) => {
    closePopupWithdrawal(e, reason);
    if (!(isProcessing || isPending || isInit) && reason !== 'backdropClick') {
      resetForm();
      setIsAgreeTerms(false);
    }
  };

  useEffect(() => {
    if (processingWithdrawal && isShowValue) {
      setValues({
        amount: processingWithdrawal.amount,
        fullName: processingWithdrawal.user.fullName,
        phoneNumber: processingWithdrawal.user.phoneNumber,
        email: processingWithdrawal.user.email,
        address: processingWithdrawal.user.address,
        identityNumber: processingWithdrawal.user.identityNumber,
        taxCode: processingWithdrawal.user.taxCode,
        identityFrontImageUrl: processingWithdrawal.user.identityFrontImageUrl,
        identityBackImageUrl: processingWithdrawal.user.identityBackImageUrl,
        digitalSignature: processingWithdrawal.user.digitalSignature,
        bankName: processingWithdrawal.bank.name,
        accountNumber: processingWithdrawal.bank.accountNumber,
        accountOwner: processingWithdrawal.bank.accountOwner,
        dateOfIssue: convertFormatDate(processingWithdrawal.user.dateOfIssue),
        placeOfIssue: processingWithdrawal.user.placeOfIssue,
      });
      setIsAgreeTerms(true);
    }
    if (!processingWithdrawal && paidTransaction) {
      setValues({
        amount: '',
        fullName: paidTransaction.user.fullName,
        phoneNumber: paidTransaction.user.phoneNumber,
        email: paidTransaction.user.email,
        address: paidTransaction.user.address,
        identityNumber: paidTransaction.user.identityNumber,
        taxCode: paidTransaction.user.taxCode,
        identityFrontImageUrl: paidTransaction.user.identityFrontImageUrl,
        identityBackImageUrl: paidTransaction.user.identityBackImageUrl,
        digitalSignature: paidTransaction.user.digitalSignature,
        bankName: paidTransaction.bank.name,
        accountNumber: paidTransaction.bank.accountNumber,
        accountOwner: paidTransaction.bank.accountOwner,
        dateOfIssue: convertFormatDate(paidTransaction.user.dateOfIssue),
        placeOfIssue: paidTransaction.user.placeOfIssue,
      });
      setIsAutoFillPaidTxn(true);
    }
  }, [processingWithdrawal, paidTransaction]);

  // For a new request (fresh or reusing a paid transaction), auto-fill the full
  // available balance. The amount field is read-only, so the user always
  // withdraws their entire balance.
  useEffect(() => {
    if (processingWithdrawal || isShowValue || !isOpenPopupWithdrawal) return;
    handleChange(balance)('amount');
  }, [
    isOpenPopupWithdrawal,
    isShowValue,
    balance,
    processingWithdrawal,
    paidTransaction,
  ]);

  const handleScrollToError = (newErrors) => {
    const err = Object.keys(newErrors || {});
    if (err.length) {
      setTimeout(() => {
        const input = document.querySelector(`[aria-invalid="true"]`);
        input?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
          inline: 'start',
        });
      }, 100);
    }
  };
  useEffect(() => {
    if (isSubmitting && !isValidating) {
      handleScrollToError(errors);
    }
  }, [errors, isValidating, isSubmitting]);

  const handleAcceptPolicy = () => {
    setIsAgreeTerms(!isAgreeTerms);
  };

  const renderWithdrawalNote = () => {
    if (isShowDetail && (isReject || isCancel)) {
      const noteText = isReject
        ? t('rejectReason', { reason: processingWithdrawal?.note })
        : t('cancelWithdrawalRequestReason');

      return (
        <Box className="reject-withdrawal-note">
          <InfoRoundedIcon sx={{ color: COLOR.errorDropZone }} />
          <Typography variant="body-14-regular" color={COLOR.darkGray}>
            {noteText}
          </Typography>
        </Box>
      );
    }
    return null;
  };

  useEffect(() => {
    const scrollContainer = contentRef.current?.closest(
      '.MuiDialogContent-root',
    );
    scrollContainer?.scrollTo({ top: 0, behavior: 'smooth' });
  }, [processingWithdrawal?.status]);

  const renderDialogContent = () => (
    <StyledWithdrawal ref={contentRef}>
      {isShowStatusStepper && (
        <StatusStepper
          activeStep={processingWithdrawal?.status}
          steps={STEP_WITHDRAWAL_STATUS}
        />
      )}
      {renderWithdrawalNote()}
      <AmountInputForm
        amount={values.amount}
        handleChangeText={handleChange}
        balance={balance}
        errors={errors}
        touched={touched}
        disabled
        isShowDetail={isShowDetail}
      />
      <PersonalInfo
        personalInfo={values}
        handleChangeText={handleChange}
        errors={errors}
        touched={touched}
        setFieldError={setFieldError}
        disabled={isShowValue || isAutoFillPaidTxn}
      />
      <BankInfo
        bankInfo={values}
        handleChangeText={handleChange}
        errors={errors}
        touched={touched}
        disabled={isShowValue || isAutoFillPaidTxn}
      />
      <WithDrawalFooter
        isAcceptPolicy={isAgreeTerms}
        handleAcceptPolicy={handleAcceptPolicy}
        disabled={isShowValue}
      />
    </StyledWithdrawal>
  );

  const renderActionButton = () => {
    if (isShowDetail && !(isPending || isInit)) return null;

    return (
      <StyledActionButton>
        {isShowDetail ? (
          (isPending || isInit) && (
            <CommonButton
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.secondary[16]}`,
                padding: '8px 16px',
              }}
              variant="outlined"
              textProps={{
                color: COLOR.indigo.default,
                variantText: 'body-14-medium',
              }}
              onClick={onCancelWithdrawal}
            >
              {t('cancelWithdrawalRequest')}
            </CommonButton>
          )
        ) : (
          <>
            <CommonButton
              customStyle={{
                height: '40px',
                borderRadius: '8px',
                border: `1px solid ${COLOR.secondary[16]}`,
              }}
              variant="outlined"
              textProps={{
                color: COLOR.indigo.default,
                variantText: 'body-14-medium',
              }}
              onClick={handleClosePopup}
            >
              {t('cancel')}
            </CommonButton>
            <CommonButton
              customStyle={{
                height: '40px',
                borderRadius: '8px',
              }}
              variant="contained"
              textProps={{
                color: COLOR.indigo.default,
                variantText: 'body-14-medium',
              }}
              disabled={!isAgreeTerms || isLoading || isShowValue}
              startIcon={
                isLoading ? (
                  <CircularProgress size={20} className="btn-loading" />
                ) : (
                  <></>
                )
              }
              onClick={handleSubmit}
            >
              {t('sendRequestWithdrawal')}
            </CommonButton>
          </>
        )}
      </StyledActionButton>
    );
  };

  return (
    <CommonDialog
      width="640px"
      fullWidth
      open={isOpenPopupWithdrawal}
      onClose={handleClosePopup}
      title={isAffiliate ? t('paymentRequest') : t('withdrawal')}
      contentComponent={renderDialogContent()}
      actionComponent={renderActionButton()}
    />
  );
};

export default Withdrawal;
